/**
 * Tenant Configuration Types
 *
 * Defines the structure for multi-tenant white-labeling support.
 * Each tenant can customize branding, features, policies, and crisis resources.
 */

import type { ConsentScope } from '../events/types';

// ============================================
// BRAND CONFIGURATION
// ============================================

/**
 * Brand color tokens for theming
 */
export interface BrandColors {
  // Primary brand colors
  primary: string;           // Main brand color
  primaryHover: string;      // Hover state
  primaryText: string;       // Text on primary background

  // Accent colors (can override defaults)
  accentGrowth?: string;     // Success/growth color
  accentCalm?: string;       // Calm/info color
  accentStress?: string;     // Stress/warning color
  accentWarning?: string;    // Warning color

  // Background overrides
  bgVoid?: string;           // Main background
  bgSurface?: string;        // Elevated surface
  bgElevated?: string;       // Card background
}

/**
 * Logo configuration
 */
export interface BrandLogos {
  // Primary logo (used in header, etc.)
  primary: string;           // URL to primary logo
  primaryDark?: string;      // Dark mode variant

  // Icon/favicon
  icon: string;              // Square icon (used for favicon, PWA)
  iconDark?: string;         // Dark mode variant

  // Additional sizes for PWA
  icon192?: string;          // 192x192 for PWA
  icon512?: string;          // 512x512 for PWA
  maskable?: string;         // Maskable icon for PWA
}

/**
 * Typography overrides
 */
export interface BrandTypography {
  fontFamily?: string;       // Primary font family
  fontFamilyDisplay?: string; // Display/heading font
  fontFamilyMono?: string;   // Monospace font
}

/**
 * Complete brand configuration
 */
export interface BrandConfig {
  name: string;              // Brand name (e.g., "Resilience Forge")
  shortName: string;         // Short name for PWA (max 12 chars)
  tagline?: string;          // Brand tagline

  colors: BrandColors;
  logos: BrandLogos;
  typography?: BrandTypography;

  // PWA specific
  themeColor: string;        // Browser theme color
  backgroundColor: string;   // PWA splash background
}

// ============================================
// FEATURE FLAGS
// ============================================

/**
 * Feature flags for capability profiles
 */
export interface FeatureFlags {
  // Core features
  aiCoachEnabled: boolean;
  communityEnabled: boolean;
  journalEnabled: boolean;
  goalsEnabled: boolean;
  brainGymEnabled: boolean;

  // Advanced features
  assessmentsEnabled: boolean;
  parentPortalEnabled: boolean;
  counselorDashboardEnabled: boolean;

  // Engagement features
  gamificationEnabled: boolean;
  achievementsEnabled: boolean;
  shopEnabled: boolean;

  // Safety features
  crisisResourcesEnabled: boolean;
  escalationEnabled: boolean;
}

/**
 * Default feature flags (all enabled)
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  aiCoachEnabled: true,
  communityEnabled: false, // Off by default until moderation is ready
  journalEnabled: true,
  goalsEnabled: true,
  brainGymEnabled: true,
  assessmentsEnabled: true,
  parentPortalEnabled: false,
  counselorDashboardEnabled: true,
  gamificationEnabled: true,
  achievementsEnabled: true,
  shopEnabled: true,
  crisisResourcesEnabled: true,
  escalationEnabled: true,
};

// ============================================
// CRISIS RESOURCES
// ============================================

/**
 * Crisis resource contact
 */
export interface CrisisResource {
  id: string;
  type: 'hotline' | 'text_line' | 'chat' | 'counselor' | 'emergency';
  name: string;
  description: string;

  // Contact info (at least one required)
  phone?: string;
  sms?: string;
  smsBody?: string;          // Pre-filled SMS body
  url?: string;
  email?: string;

  // Display
  priority: number;          // Lower = higher priority
  available24x7: boolean;
  localOnly?: boolean;       // Only for this tenant/region
}

/**
 * Crisis resources configuration
 */
export interface CrisisConfig {
  // National/default resources (always shown)
  nationalResources: CrisisResource[];

  // Tenant-specific resources (school counselor, etc.)
  localResources: CrisisResource[];

  // Custom messaging
  headerMessage?: string;
  subheaderMessage?: string;
}

/**
 * Default crisis resources (US-based)
 */
export const DEFAULT_CRISIS_RESOURCES: CrisisConfig = {
  nationalResources: [
    {
      id: '988-lifeline',
      type: 'hotline',
      name: '988 Suicide & Crisis Lifeline',
      description: 'Free, confidential support 24/7',
      phone: '988',
      priority: 1,
      available24x7: true,
    },
    {
      id: 'crisis-text-line',
      type: 'text_line',
      name: 'Crisis Text Line',
      description: 'Text HOME to 741741',
      sms: '741741',
      smsBody: 'HOME',
      priority: 2,
      available24x7: true,
    },
  ],
  localResources: [],
};

// ============================================
// POLICY CONFIGURATION
// ============================================

/**
 * Age-related policy settings
 */
export interface AgePolicy {
  minimumAge: number;        // Minimum age to use app
  coppaAge: number;          // Age requiring parental consent (usually 13)
  adultAge: number;          // Age of majority
}

/**
 * Data retention policy
 */
export interface RetentionPolicy {
  eventRetentionDays: number;      // How long to keep events
  journalRetentionDays: number;    // How long to keep journal entries
  chatRetentionDays: number;       // How long to keep AI chat history
  deletionGracePeriodDays: number; // Grace period before permanent deletion
}

/**
 * Consent requirements
 */
export interface ConsentPolicy {
  defaultScope: ConsentScope;
  requireExplicitConsent: boolean;
  allowScopeChanges: boolean;
  parentalConsentRequired: boolean; // For under-COPPA users
}

/**
 * Complete policy configuration
 */
export interface PolicyConfig {
  age: AgePolicy;
  retention: RetentionPolicy;
  consent: ConsentPolicy;

  // Legal
  termsUrl?: string;
  privacyUrl?: string;
  coppaNoticeUrl?: string;

  // Custom policy text
  dataUsageNotice?: string;
}

/**
 * Default policy configuration
 */
export const DEFAULT_POLICY_CONFIG: PolicyConfig = {
  age: {
    minimumAge: 10,
    coppaAge: 13,
    adultAge: 18,
  },
  retention: {
    eventRetentionDays: 365,
    journalRetentionDays: 730, // 2 years
    chatRetentionDays: 90,
    deletionGracePeriodDays: 30,
  },
  consent: {
    defaultScope: 'personal_only',
    requireExplicitConsent: true,
    allowScopeChanges: true,
    parentalConsentRequired: true,
  },
};

// ============================================
// COPY / CONTENT OVERRIDES
// ============================================

/**
 * Customizable text content
 */
export interface CopyOverrides {
  // Welcome/onboarding
  welcomeTitle?: string;
  welcomeSubtitle?: string;
  welcomeDescription?: string;

  // App chrome
  appTitle?: string;
  footerText?: string;

  // Feature names (for white-labeling)
  aiCoachName?: string;      // e.g., "Mindset Coach" or "Your Guide"
  journalName?: string;      // e.g., "Journal" or "Reflection Log"
  brainGymName?: string;     // e.g., "Brain Gym" or "Focus Games"
}

// ============================================
// SUBSCRIPTION CONFIGURATION
// ============================================

/**
 * Subscription plan identifiers
 */
export type SubscriptionPlanId =
  | 'free'
  | 'individual_pro'
  | 'family'
  | 'school'
  | 'district';

/**
 * Subscription settings for a tenant
 */
export interface SubscriptionConfig {
  // Plans available for this tenant
  allowedPlans: SubscriptionPlanId[];

  // Default plan for new users
  defaultPlan: SubscriptionPlanId;

  // Trial settings
  trialDays?: number;
  trialPlan?: SubscriptionPlanId;

  // Feature gating by plan
  planFeatures: Record<SubscriptionPlanId, Partial<FeatureFlags>>;
}

/**
 * Default subscription configuration
 */
export const DEFAULT_SUBSCRIPTION_CONFIG: SubscriptionConfig = {
  allowedPlans: ['free', 'individual_pro', 'family', 'school', 'district'],
  defaultPlan: 'free',
  trialDays: 7,
  trialPlan: 'individual_pro',
  planFeatures: {
    free: {
      aiCoachEnabled: false,
      brainGymEnabled: false,
      parentPortalEnabled: false,
      counselorDashboardEnabled: false,
      communityEnabled: false,
    },
    individual_pro: {
      aiCoachEnabled: true,
      brainGymEnabled: true,
      parentPortalEnabled: false,
      counselorDashboardEnabled: false,
    },
    family: {
      aiCoachEnabled: true,
      brainGymEnabled: true,
      parentPortalEnabled: true,
      counselorDashboardEnabled: false,
    },
    school: {
      aiCoachEnabled: true,
      brainGymEnabled: true,
      parentPortalEnabled: true,
      counselorDashboardEnabled: true,
    },
    district: {
      aiCoachEnabled: true,
      brainGymEnabled: true,
      parentPortalEnabled: true,
      counselorDashboardEnabled: true,
    },
  },
};

// ============================================
// COMPLETE TENANT CONFIGURATION
// ============================================

/**
 * Complete tenant configuration
 */
export interface TenantConfig {
  // Identity
  id: string;                // Unique tenant identifier
  slug: string;              // URL-safe identifier
  displayName: string;       // Human-readable name

  // Branding
  brand: BrandConfig;

  // Features
  features: FeatureFlags;

  // Safety
  crisis: CrisisConfig;

  // Policy
  policy: PolicyConfig;

  // Subscription
  subscription: SubscriptionConfig;

  // Content
  copy?: CopyOverrides;

  // Metadata
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

/**
 * Tenant resolution result
 */
export interface TenantResolution {
  tenant: TenantConfig;
  source: 'hostname' | 'cohort_code' | 'invite_token' | 'default';
  matchedValue?: string;     // The hostname/code/token that matched
}

// ============================================
// DEFAULT TENANT
// ============================================

/**
 * Default tenant configuration (fallback)
 */
export const DEFAULT_TENANT: TenantConfig = {
  id: 'default',
  slug: 'default',
  displayName: 'Resilience Forge',

  brand: {
    name: 'Resilience Forge',
    shortName: 'Resilience',
    tagline: 'Small steps. Useful skills.',

    colors: {
      primary: '#10b981',      // Emerald green
      primaryHover: '#059669',
      primaryText: '#0a0a0a',
      accentGrowth: '#afdccb',
      accentCalm: '#afdccb',
      accentStress: '#f97316',
      accentWarning: '#eab308',
    },

    logos: {
      primary: '/forge-icon.svg',
      icon: '/forge-icon.svg',
      icon192: '/icon-192.svg',
      icon512: '/icon-512.svg',
    },

    themeColor: '#10b981',
    backgroundColor: '#0a0a0a',
  },

  features: DEFAULT_FEATURE_FLAGS,
  crisis: DEFAULT_CRISIS_RESOURCES,
  policy: DEFAULT_POLICY_CONFIG,
  subscription: DEFAULT_SUBSCRIPTION_CONFIG,

  createdAt: '2024-01-01T00:00:00Z',
  updatedAt: '2024-01-01T00:00:00Z',
  isActive: true,
};

